import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Layers, Target } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Select } from '../../../../../components/ui/Select';
import { GROUPS, type GroupId } from '../../../../../config/systemConfig';
import type { PracticeContentHealthReport, PracticeCapacityStatus } from '../hooks/useGovernanceData';
interface PracticeContentHealthTabProps {
  report: PracticeContentHealthReport;
  isRunning: boolean;
}
const STATUS_META: Record<PracticeCapacityStatus, {
  label: string;
  className: string;
}> = {
  critical: {
    label: 'Kritiek',
    className: 'bg-red-50 text-red-700 border-red-200'
  },
  thin: {
    label: 'Dun',
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  healthy: {
    label: 'Gezond',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
export function PracticeContentHealthTab({
  report,
  isRunning
}: PracticeContentHealthTabProps) {
  const [groupFilter, setGroupFilter] = useState<'all' | GroupId>('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const modules = useMemo(() => Array.from(new Set(report.modules.map(item => item.module))).sort(), [report.modules]);
  const visibleFamilies = report.families.filter(family => {
    if (groupFilter !== 'all' && family.group !== groupFilter) return false;
    return moduleFilter === 'all' || family.module === moduleFilter;
  });
  const visibleModules = report.modules.filter(item => {
    if (groupFilter !== 'all' && item.group !== groupFilter) return false;
    return moduleFilter === 'all' || item.module === moduleFilter;
  });
  if (isRunning && report.modules.length === 0) {
    return <div className="p-8 text-sm text-gray-500" data-id="element-5580" data-mp="ximuw6zbt5u2">Oefenstof analyseren…</div>;
  }
  return <div className="space-y-6" data-id="element-5581" data-mp="2dwmqx6v1wbc1">
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-id="element-5582" data-mp="2dvuvpx5d8fma">
        <div className="flex items-start gap-4" data-id="element-5583" data-mp="28xte7gnl8ocx">
          <div className="rounded-xl bg-indigo-100 p-3 text-indigo-700" data-id="element-5584" data-mp="2g1t1t1y25m1b">
            <Layers className="h-6 w-6" data-id="element-5585" data-mp="2g3vqninh594g" />
          </div>
          <div data-id="element-5586" data-mp="2kzunlumvbzgh">
            <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700" data-id="element-5587" data-mp="2kzb3q8dnr0zt">
              Practice capacity
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-900" data-id="element-5588" data-mp="2uz1zeddyfm1h">
              Oefenstof-capaciteit
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600" data-id="element-5589" data-mp="1xbtjqz0mx77e">
              Waar kan adaptief oefenen nog veilig variëren? Dit meet bruikbare,
              unieke vragen per family binnen de tiers van een groep — geen
              mastery-score en geen blokkade voor leerlingen.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-5590" data-mp="2c2aj8ctpln1n">
        <Metric label="Kritieke families" value={report.counts.critical} tone="red" data-id="element-5591" data-mp="1ejr15i4fngui" />
        
        <Metric label="Dunne families" value={report.counts.thin} tone="amber" data-id="element-5592" data-mp="1jiv7m0zz30h5" />
        
        <Metric label="Gezonde families" value={report.counts.healthy} tone="emerald" data-id="element-5593" data-mp="p23szsdfqu3" />
        
      </div>

      <Card className="p-4" data-id="element-5594" data-mp="3stmgx8l2astn">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-5595" data-mp="3stco7d8mu7k9">
          <Select value={String(groupFilter)} onChange={event => setGroupFilter(event.target.value === 'all' ? 'all' : Number(event.target.value) as GroupId)} options={[{
          value: 'all',
          label: 'Alle groepen'
        }, ...GROUPS.map(group => ({
          value: String(group),
          label: `Groep ${group}`
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5596" data-mp="3ns6vhfez2kaq" />
          
          <Select value={moduleFilter} onChange={event => setModuleFilter(event.target.value)} options={[{
          value: 'all',
          label: 'Alle modules'
        }, ...modules.map(module => ({
          value: module,
          label: module
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5597" data-mp="353s4ut8kh8ag" />
          
          <p className="text-xs text-slate-500 sm:ml-auto" data-id="element-5598" data-mp="2q6huxobkoagu">
            Kritiek: 0–3 vragen · dun: 4–6 · gezond: 7+
          </p>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-5599" data-mp="z1xurilql3j4">
        <div className="border-b border-slate-100 p-5" data-id="element-5600" data-mp="z0wijasziazt">
          <h3 className="text-sm font-semibold text-slate-900" data-id="element-5601" data-mp="kdjvtirfxz7u">
            Praktijkruimte per groep × module
          </h3>
          <p className="mt-1 text-xs text-slate-500" data-id="element-5602" data-mp="pajz5lwwonv9">
            Rode combinaties bevatten families die direct tot fallback,
            herhaling of leerdoel-drift kunnen leiden.
          </p>
        </div>
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3" data-id="element-5603" data-mp="gcriadbjd85r">
          {visibleModules.map(item => {
          const meta = STATUS_META[item.status];
          return <div key={`${item.group}-${item.module}`} className="bg-white p-4" data-id="element-5604" data-mp="6e20ekjn3npi">
                
                <div className="flex items-center justify-between gap-2" data-id="element-5605" data-mp="1vt02075gb8s6">
                  <p className="font-mono text-xs font-bold text-slate-800" data-id="element-5606" data-mp="20rjr2gfcd0or">
                    G{item.group} · {item.module}
                  </p>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.className}`} data-id="element-5607" data-mp="3l9c48kodgy5c">
                    
                    {meta.label}
                  </span>
                </div>
                <p className="mt-3 text-2xl font-bold tabular-nums text-slate-900" data-id="element-5608" data-mp="3v0yqdc290hmv">
                  {item.totalQuestions}
                </p>
                <p className="text-xs text-slate-500" data-id="element-5609" data-mp="7tejbe6eqief">
                  vragen · {item.familiesWithContent}/{item.eligibleFamilies}{' '}
                  families met inhoud
                </p>
                <p className="mt-2 text-xs text-slate-600" data-id="element-5610" data-mp="hsvjxh6sy63j">
                  {item.criticalFamilies} kritiek · {item.thinFamilies} dun
                </p>
              </div>;
        })}
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-5611" data-mp="2jixe1mgwq5yz">
        <div className="flex items-start gap-3 border-b border-slate-100 p-5" data-id="element-5612" data-mp="2jlt9n45a8ik9">
          <Target className="mt-0.5 h-4 w-4 text-red-500" data-id="element-5613" data-mp="2em6v8rdpww9v" />
          <div data-id="element-5614" data-mp="ii0rz51rpph2">
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-5615" data-mp="iiizixtvrq4a">
              Auteurprioriteit
            </h3>
            <p className="mt-1 text-xs text-slate-500" data-id="element-5616" data-mp="nhdgdu8af4lr">
              Kritiek vraagt eerst om inhoud. Dunne families zijn daarna
              uitbreidingswerk: zeven unieke varianten is de minimumbasis; een
              gerichte sessie van tien vragen vraagt nog steeds om herhaling.
            </p>
          </div>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-5617" data-mp="11e8z6eeccaf0">
          {visibleFamilies.filter(family => family.status !== 'healthy').map(family => {
          const meta = STATUS_META[family.status];
          return <div key={family.key} className="flex items-center gap-3 p-4" data-id="element-5618" data-mp="1b89ch3js0lyt">
                  {family.status === 'critical' ? <AlertTriangle className="h-4 w-4 flex-none text-red-500" data-id="element-5619" data-mp="1k6uumxlc5yz2" /> : <AlertTriangle className="h-4 w-4 flex-none text-amber-500" data-id="element-5620" data-mp="1k8ecutqjyk3h" />}
                  <div className="min-w-0 flex-1" data-id="element-5621" data-mp="1p6zb4nm8utpa">
                    <p className="font-mono text-xs font-semibold text-slate-900" data-id="element-5622" data-mp="1z9uw1q9hypup">
                      G{family.group} · {family.familyId}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-5623" data-mp="1u96wuhb472pe">
                      {family.questionCount} bruikbare vragen
                      {family.questionTypes.length > 0 && ` · ${family.questionTypes.join(', ')}`}
                    </p>
                    {family.sessionRepeatCount > 0 && <p className="mt-1 text-xs text-slate-500" data-id="element-5624" data-mp="2hz166ow8694w">
                        In een focusset van 10: minimaal{' '}
                        {family.sessionRepeatCount} herhaling
                        {family.sessionRepeatCount === 1 ? '' : 'en'} zonder
                        nieuwe varianten
                      </p>}
                  </div>
                  <span className={`rounded-full border px-2 py-1 text-[10px] font-bold ${meta.className}`} data-id="element-5625" data-mp="30qtzylj4bydo">
                  
                    +{family.questionsNeeded} naar minimum
                  </span>
                </div>;
        })}
          {visibleFamilies.every(family => family.status === 'healthy') && <div className="flex items-center gap-2 p-6 text-sm text-emerald-700" data-id="element-5626" data-mp="3kem7zsd2nk6x">
              <CheckCircle2 className="h-5 w-5" data-id="element-5627" data-mp="3kgp3ut0wo7jk" />
              Geen dunne of kritieke families binnen deze selectie.
            </div>}
        </div>
      </Card>
    </div>;
}
function Metric({
  label,
  value,
  tone
}: {
  label: string;
  value: number;
  tone: 'red' | 'amber' | 'emerald';
}) {
  const colors = {
    red: 'border-red-100 bg-red-50 text-red-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700'
  }[tone];
  return <div className={`rounded-xl border p-4 ${colors}`} data-id="element-5628" data-mp="3tfkdyg72m1s5">
      <p className="text-2xl font-bold tabular-nums" data-id="element-5629" data-mp="3tdzi8n90scsp">{value}</p>
      <p className="mt-1 text-xs font-medium" data-id="element-5630" data-mp="2d0suisrwk4uz">{label}</p>
    </div>;
}